/* eslint-disable import/no-unused-modules */
import {
  captureException,
  captureMessage,
  getCurrentScope,
  getReplay,
  init,
  withScope,
} from '@sentry/react';
import React from 'react';
import {
  createRoutesFromChildren,
  matchRoutes,
  useLocation,
  useNavigationType,
} from 'react-router';
import supportedBrowsers from './app/supportedBrowsers';

const TRACING_SAMPLE_RATE = 0.1;

function initSentry({ enableTracing }: { enableTracing: boolean }) {
  const shouldEnableSentry = process.env.NODE_ENV === 'production';
  const { __slab } = window;
  const slabUrlRegex = new RegExp(`https://(.+\\.)?${__slab.HOST}`, 'i');

  init({
    enabled: shouldEnableSentry,
    attachStacktrace: true, // so `captureMessage` also have a stacktrace
    debug: false,
    release: __slab.VERSION,
    allowUrls: [document.location.host, slabUrlRegex],
    dsn: 'https://df15549ba190f45040e8ac277c3a8ffa@o59832.ingest.sentry.io/4505782805528576',
    environment: __slab.ENVIRONMENT,
    replaysOnErrorSampleRate: shouldEnableSentry ? 1 : 0,
    replaysSessionSampleRate: 0,
    tracesSampleRate: enableTracing ? 1 : 0,
  });

  window.Sentry = {
    isEnabled: shouldEnableSentry,
    withScope,
    captureMessage,
    captureException,
    getCurrentScope,
    getReplay,
  };
}

async function main() {
  const isTracingEnabled = Math.random() <= TRACING_SAMPLE_RATE;
  initSentry({ enableTracing: isTracingEnabled });

  try {
    const [{ default: app }, { default: initTracing }] = await Promise.all([
      await import(
        /* webpackChunkName: "app" */
        './app/app'
      ),
      window.Sentry?.isEnabled && isTracingEnabled
        ? await import('./initTracing').catch(() => ({ default: null }))
        : Promise.resolve({ default: null }),
    ]);

    initTracing?.({
      useEffect: React.useEffect,
      useLocation,
      useNavigationType,
      createRoutesFromChildren,
      matchRoutes,
    });
    await app();
  } catch (e) {
    const isSupportedBrowser = supportedBrowsers.test(navigator.userAgent);

    captureException(e, {
      level: isSupportedBrowser ? 'fatal' : 'error',
    });

    const browserPrompt = isSupportedBrowser
      ? '<p>The error has been logged and we’ll be on it!</p>'
      : '<p>Your browser may be outdated or not supported by Slab.</p>';

    const root = document.getElementById('root') || document.body;

    if (root) {
      root.innerHTML = `
      <div style="margin:12px;font-family:sans-serif">
        <h2>Oops! Something went wrong.</h2>
        ${browserPrompt}
      </div>
    `;
    }

    throw e;
  }

  const initReplay = (await import('./initReplay')).default;
  initReplay();
}

main();
